// libraries
import React, { useState, useCallback, useEffect } from "react";

// components
import Section from "/react4xp/components/Section";
import SearchForm from "./SearchForm";
import SearchResults from "./SearchResults";
import Filters, { type DateOption, type FilterGroupData } from "./Filters";
import type { SearchResultItem } from "./SearchResults";
import type { EventItem } from "/react4xp/components/EventCard";
import type { NewsArticle } from "/react4xp/components/News";

// types
export type FilterGroup = FilterGroupData;

export interface SearchComponentProps {
  // Initial data props
  initialItems?: Array<
    { type: "event"; data: EventItem } | { type: "news"; data: NewsArticle }
  >;
  nextPageUrl?: string;
  filters?: {
    type?: FilterGroupData;
    tema?: FilterGroupData;
    tag?: FilterGroupData;
  };
  dateFilter?: {
    value?: string;
    options?: DateOption[];
  };

  // Search form props
  searchValue?: string;
  searchError?: string;
  totalResults?: number | string; // Can be number or HTML string (e.g., "4 treff på <span>test</span>, <span>test 2</span>")
  searchTerm?: string;
  placeholder?: string;
  searchInputName?: string;
  minSearchLength?: number;
  minSearchLengthMessage?: string;

  // Results props
  isLoading?: boolean;
  loadMoreText?: string;

  // Layout props
  title?: string;
  className?: string;
  filtersContentId?: string;
}

export default function SearchComponent({
  initialItems,
  nextPageUrl: initialNextPageUrl,
  filters,
  dateFilter,
  searchValue: initialSearchValue,
  searchError: initialSearchError,
  totalResults,
  searchTerm,
  placeholder,
  searchInputName,
  minSearchLength = 3,
  minSearchLengthMessage = "Du må bruke minimum 3 tegn i søkestrengen",
  isLoading: externalIsLoading,
  loadMoreText,
  title = "Søk",
  className,
  filtersContentId = "filterContent",
}: SearchComponentProps) {
  // Internal state management
  const [items, setItems] = useState<SearchResultItem[]>(initialItems || []);
  const [nextPageUrl, setNextPageUrl] = useState<string | undefined>(
    initialNextPageUrl
  );
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(initialSearchValue || "");
  const [searchError, setSearchError] = useState(initialSearchError || "");
  const [urlParams, setUrlParams] = useState<URLSearchParams | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setUrlParams(new URLSearchParams(window.location.search));
    }
  }, []);

  useEffect(() => {
    setItems(initialItems || []);
    setNextPageUrl(initialNextPageUrl);
  }, [initialItems, initialNextPageUrl]);

  useEffect(() => {
    if (initialSearchValue !== undefined) {
      setSearchValue(initialSearchValue);
    }
  }, [initialSearchValue]);

  useEffect(() => {
    if (initialSearchError !== undefined) {
      setSearchError(initialSearchError);
    }
  }, [initialSearchError]);

  const handleLoadMore = useCallback(async () => {
    if (isLoading || !nextPageUrl) return;

    setIsLoading(true);
    try {
      const response = await fetch(nextPageUrl);
      if (!response.ok) {
        throw new Error("Failed to load more results");
      }
      const data = await response.json();

      if (data.items && Array.isArray(data.items)) {
        setItems((prevItems) => [...prevItems, ...data.items]);
      }
      setNextPageUrl(data.nextPageUrl);
    } catch (error) {
      console.error("Error loading more results:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, nextPageUrl]);

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmedValue = searchValue.trim();

    if (trimmedValue.length < minSearchLength) {
      setSearchError(minSearchLengthMessage);
      return;
    }

    setSearchError("");

    // Redirect with search parameter
    const params = new URLSearchParams(window.location.search);
    if (trimmedValue) {
      params.set("s", trimmedValue);
    } else {
      params.delete("s");
    }

    const newSearch = params.toString();
    const newUrl =
      window.location.pathname + (newSearch ? "?" + newSearch : "");
    window.location.href = newUrl;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    // Clear error when user starts typing
    if (searchError) {
      setSearchError("");
    }
  };

  const handleFilterChange = useCallback(
    (filterId: string, paramType: "type" | "tema" | "tag") => {
      const currentSearch = window.location.search;
      const params = new URLSearchParams(currentSearch);
      const currentValue = params.get(paramType);

      if (currentValue) {
        const ids = currentValue.split(";");
        const index = ids.indexOf(filterId);

        if (index > -1) {
          // Remove if already selected
          ids.splice(index, 1);
          if (ids.length > 0) {
            params.set(paramType, ids.join(";"));
          } else {
            params.delete(paramType);
          }
        } else {
          // Add if not selected
          params.set(paramType, currentValue + ";" + filterId);
        }
      } else {
        // First selection for this filter type
        params.set(paramType, filterId);
      }

      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? "?" + newSearch : "");
      window.location.href = newUrl;
    },
    []
  );

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const params = new URLSearchParams(window.location.search);
      const dateValue = e.target.value;

      if (dateValue) {
        params.set("date", dateValue);
      } else {
        params.delete("date");
      }

      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? "?" + newSearch : "");
      window.location.href = newUrl;
    },
    []
  );

  // Use provided filters directly, no fallbacks
  const activeFilters = filters || {};

  // Get selected filter IDs from URL params
  const selectedFilterIds = {
    type: urlParams?.get("type")?.split(";") || [],
    tema: urlParams?.get("tema")?.split(";") || [],
    tag: urlParams?.get("tag")?.split(";") || [],
  };

  // Use external isLoading if provided, otherwise use internal
  const currentIsLoading =
    externalIsLoading !== undefined ? externalIsLoading : isLoading;

  return (
    <Section
      bgColor="white"
      paddingTop="big"
      paddingBottom="big"
      className={className || "search-page"}
    >
      <div className="container">
        <div className="row search-page__content">
          <div className="col-12 col-xl-8 col-lg-9">
            <h1 className="h1">{title}</h1>

            <SearchForm
              value={searchValue}
              onChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              error={searchError}
              totalResults={totalResults}
              searchTerm={searchTerm || searchValue}
              placeholder={placeholder}
              name={searchInputName}
            />

            <SearchResults
              items={items}
              nextPageUrl={nextPageUrl}
              isLoading={currentIsLoading}
              onLoadMore={handleLoadMore}
              loadMoreText={loadMoreText}
            />
          </div>

          <div className="col-12 col-lg-3 offset-xl-1">
            <Filters
              title="Filter"
              showFiltersLabel="Vis filter"
              hideFiltersLabel="Skjul filter"
              filters={activeFilters}
              dateFilter={{
                value:
                  dateFilter?.value || urlParams?.get("date") || "",
                options: dateFilter?.options,
              }}
              selectedFilterIds={selectedFilterIds}
              onFilterChange={handleFilterChange}
              onDateChange={handleDateChange}
              showOnlyDateFilter={!items || items.length === 0}
              filtersContentId={filtersContentId}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}
